import React, { useState, useEffect } from 'react';
import { ShoppingCart, Heart, Eye, ChevronLeft, ChevronRight, ArrowLeft } from 'lucide-react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { useFavorites } from '../../context/FavoritesContext';
import Card from '../common/Card';
import Button from '../common/Button';
import Toast from '../common/Toast';
import productService from '../../services/productService';

const CategoryDetailsPage = () => {
    const { categoryName } = useParams();
    const navigate = useNavigate();
    const { addToCart } = useCart();
    const { toggleFavorite, isFavorite } = useFavorites();
    const [products, setProducts] = useState([]);
    const [categoryDetails, setCategoryDetails] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 20;

    useEffect(() => {
        const loadData = async () => {
            try {
                const [productsData, categoriesData] = await Promise.all([
                    productService.getProducts(),
                    productService.getCategories()
                ]);
                
                const cat = categoriesData.find(c => String(c.id) === categoryName || c.name === categoryName);
                
                let categoryProducts = [];
                if (cat) {
                    categoryProducts = productsData.filter(p => String(p.category) === String(cat.id));
                } else {
                    categoryProducts = productsData.filter(p => String(p.category) === categoryName || String(p.category_name) === categoryName);
                }
                
                setProducts(categoryProducts);
                if (cat) {
                    setCategoryDetails(cat.name);
                } else if (categoryProducts.length > 0 && categoryProducts[0].category_name) {
                    setCategoryDetails(categoryProducts[0].category_name);
                } else {
                    setCategoryDetails(categoryName === 'null' || !categoryName ? 'Général' : categoryName);
                }
                
                setLoading(false);
            } catch (err) {
                console.error('Error fetching data:', err);
                setError('Impossible de charger les produits. Veuillez réessayer plus tard.');
                setLoading(false);
            }
        };

        loadData();
    }, [categoryName]);

    const handleAddToCart = (product) => {
        const wasAdded = addToCart(product);
        if (wasAdded) {
            setToastMessage("Produit ajouté au panier !");
        } else {
            setToastMessage("Ce produit est déjà dans votre panier.");
        }
        setShowToast(true);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const totalPages = Math.ceil(products.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const visibleProducts = products.slice(startIndex, startIndex + itemsPerPage);

    return (
        <div className="pt-32 pb-24 bg-slate-50 min-h-screen">
            <div className="container mx-auto px-6">
                {/* Header */}
                <div className="max-w-3xl mb-12">
                    <button 
                        onClick={() => navigate('/categories')}
                        className="flex items-center gap-2 text-brand hover:text-brand-dark mb-6 transition-colors group"
                    >
                        <ArrowLeft className="w-5 h-5 transition-transform group-hover:-translate-x-1" />
                        <span className="font-bold">Retour aux catégories</span>
                    </button>
                    <h1 className="text-sm font-bold tracking-[0.2em] text-brand uppercase mb-4">Catégorie</h1>
                    <h2 className="text-4xl md:text-6xl font-extrabold text-brand-dark leading-tight mb-8">
                        {categoryDetails || 'Chargement...'}
                    </h2>
                    {!loading && !error && (
                        <span className="px-4 py-2 bg-brand-dark/5 text-brand-dark/60 text-sm font-bold rounded-full uppercase tracking-widest">
                            {products.length} Articles
                        </span>
                    )}
                </div>

                {loading ? (
                    <div className="flex flex-col items-center justify-center py-32 space-y-6">
                        <div className="w-16 h-16 border-4 border-brand/20 border-t-brand rounded-full animate-spin" />
                        <p className="text-brand-dark/60 font-medium animate-pulse">Chargement des produits...</p>
                    </div>
                ) : error ? (
                    <div className="text-center py-24 bg-white rounded-[3rem] border border-slate-100 shadow-xl max-w-2xl mx-auto px-10">
                        <div className="w-20 h-20 bg-red-50 text-red-500 rounded-3xl flex items-center justify-center mx-auto mb-8">
                            <Eye className="w-10 h-10 opacity-50" />
                        </div>
                        <h3 className="text-2xl font-bold text-brand-dark mb-4">Oups !</h3>
                        <p className="text-slate-500 mb-8">{error}</p>
                        <Button variant="primary" onClick={() => window.location.reload()}>Réessayer</Button>
                    </div>
                ) : products.length === 0 ? (
                    <div className="text-center py-24 bg-white rounded-[3rem] border border-slate-100 shadow-xl max-w-2xl mx-auto px-10">
                        <h3 className="text-2xl font-bold text-brand-dark mb-4">Aucun produit</h3>
                        <p className="text-slate-500">Cette catégorie ne contient aucun produit pour le moment.</p>
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-10">
                            {visibleProducts.map((product) => (
                                <Card key={product.id} className="group h-full flex flex-col bg-white border border-slate-100 hover:shadow-2xl hover:shadow-brand-dark/10 transition-all duration-500 rounded-[2rem] overflow-hidden">
                                    <div className="relative aspect-[4/5] overflow-hidden">
                                        <img
                                            src={product.image}
                                            alt={product.name}
                                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                        />

                                        {/* Stock Badge */}
                                        {product.stock === 0 && (
                                            <div className="absolute top-6 left-6 z-10">
                                                <span className="px-4 py-2 bg-red-500 text-white text-[10px] font-black uppercase tracking-[0.2em] rounded-full shadow-lg">
                                                    Épuisé
                                                </span>
                                            </div>
                                        )}

                                        {/* Quick Actions Overlay */}
                                        <div className="absolute inset-0 bg-brand-dark/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-4">
                                            <Link to={`/product/${product.id}`} className="w-12 h-12 bg-white text-brand-dark rounded-full flex items-center justify-center hover:bg-brand-dark hover:text-white transition-all transform translate-y-4 group-hover:translate-y-0 duration-500 shadow-xl">
                                                <Eye className="w-5 h-5" />
                                            </Link>
                                            <button
                                                onClick={() => toggleFavorite(product)}
                                                className={`w-12 h-12 rounded-full flex items-center justify-center transition-all transform translate-y-4 group-hover:translate-y-0 duration-500 delay-100 shadow-xl ${isFavorite(product.id)
                                                    ? 'bg-brand text-white'
                                                    : 'bg-white text-brand-dark hover:bg-brand-dark hover:text-white'
                                                    }`}
                                            >
                                                <Heart className={`w-5 h-5 ${isFavorite(product.id) ? 'fill-current' : ''}`} />
                                            </button>
                                            <button
                                                onClick={() => product.stock > 0 && handleAddToCart(product)}
                                                disabled={product.stock === 0}
                                                className={`w-12 h-12 rounded-full flex items-center justify-center transition-all transform translate-y-4 group-hover:translate-y-0 duration-500 delay-200 shadow-xl ${product.stock === 0 ? 'bg-slate-200 text-slate-400 cursor-not-allowed' : 'bg-white text-brand-dark hover:bg-brand-dark hover:text-white'}`}
                                            >
                                                <ShoppingCart className="w-5 h-5" />
                                            </button>
                                        </div>
                                    </div>

                                    <div className="p-8 flex flex-col flex-grow">
                                        <div className="mb-6">
                                            <Link to={`/product/${product.id}`}>
                                                <h4 className="text-xl sm:text-2xl font-extrabold text-brand-dark group-hover:text-brand transition-colors leading-tight mb-3">
                                                    {product.name}
                                                </h4>
                                            </Link>
                                            <div className="flex items-center gap-2">
                                                <span className="text-2xl font-black text-brand">
                                                    {Math.floor(product.price).toLocaleString('fr-FR')} <span className="text-xs font-bold text-slate-400 uppercase">fcfa</span>
                                                </span>
                                            </div>
                                        </div>

                                        <div className="mt-auto">
                                            <button
                                                onClick={() => product.stock > 0 && handleAddToCart(product)}
                                                disabled={product.stock === 0}
                                                className={`w-full h-14 border-2 rounded-2xl text-sm font-bold transition-all duration-500 flex items-center justify-center gap-3 group/btn ${product.stock === 0
                                                    ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
                                                    : 'bg-brand/5 text-brand-dark border-brand/10 hover:bg-brand-dark hover:text-white hover:border-brand-dark'
                                                    }`}
                                            >
                                                <ShoppingCart className="w-5 h-5 transition-transform group-hover/btn:scale-110" />
                                                {product.stock === 0 ? 'Rupture de Stock' : 'Ajouter au Panier'}
                                            </button>
                                        </div>
                                    </div>
                                </Card>
                            ))}
                        </div>

                        {/* Numeric Pagination */}
                        {totalPages > 1 && (
                            <div className="mt-16 flex items-center justify-center gap-2">
                                <button
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all ${currentPage === 1 ? 'opacity-30 cursor-not-allowed text-brand-dark/40' : 'bg-white border border-brand-dark/10 text-brand-dark hover:bg-brand-dark hover:text-white shadow-sm'}`}
                                >
                                    <ChevronLeft className="w-5 h-5" />
                                </button>

                                {[...Array(totalPages)].map((_, i) => (
                                    <button
                                        key={i}
                                        onClick={() => handlePageChange(i + 1)}
                                        className={`w-12 h-12 rounded-2xl font-bold text-sm transition-all ${currentPage === i + 1 ? 'bg-brand-dark text-white shadow-xl shadow-brand-dark/20' : 'bg-white border border-brand-dark/10 text-brand-dark hover:border-brand hover:text-brand shadow-sm'}`}
                                    >
                                        {i + 1}
                                    </button>
                                ))}

                                <button
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all ${currentPage === totalPages ? 'opacity-30 cursor-not-allowed text-brand-dark/40' : 'bg-white border border-brand-dark/10 text-brand-dark hover:bg-brand-dark hover:text-white shadow-sm'}`}
                                >
                                    <ChevronRight className="w-5 h-5" />
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>

            <Toast
                message={toastMessage}
                isOpen={showToast}
                onClose={() => setShowToast(false)}
            />
        </div>
    );
};

export default CategoryDetailsPage;
